import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import Toast from '../components/Toast';
import { useAdminTours } from '../hooks/useAdminTours';
import AdminTourForm from '../components/admin/AdminTourForm';
import AdminTourList from '../components/admin/AdminTourList';

export default function AdminToursPage() {
    const { t, i18n } = useTranslation();

    const {
        agencies,
        cities,
        propertyTypes,
        propertyIds,
        newTour,
        setNewTour,
        editingTour,
        setEditingTour,
        uploadProgress,
        tourIdFilter,
        setTourIdFilter,
        agencyNameFilter,
        setAgencyNameFilter,
        toast,
        setToast,
        isEditTourModalOpen,
        setIsEditTourModalOpen,
        isDeleteTourModalOpen,
        setIsDeleteTourModalOpen,
        tourToDelete,
        setTourToDelete,
        editTourSource,
        setEditTourSource,
        editTourFile,
        setEditTourFile,
        fileInputRef,
        editFileInputRef,
        handleEditTourClick,
        handleDeleteTour,
        handleNewTourSubmit,
        handleEditTourSubmit,
        confirmDeleteTour,
        filteredTours
    } = useAdminTours(t, i18n);

    return (
        <div className="p-6 space-y-6">
            {/* --- New Tour Form --- */}
            <AdminTourForm
                t={t}
                i18n={i18n}
                newTour={newTour}
                setNewTour={setNewTour}
                agencies={agencies}
                propertyIds={propertyIds}
                handleNewTourSubmit={handleNewTourSubmit}
                uploadProgress={uploadProgress}
                fileInputRef={fileInputRef}
            />

            {/* --- Tours List and Filters --- */}
            <AdminTourList
                t={t}
                i18n={i18n}
                filteredTours={filteredTours}
                agencies={agencies}
                propertyTypes={propertyTypes}
                cities={cities}
                handleEditTourClick={handleEditTourClick}
                handleDeleteTour={handleDeleteTour}
                tourIdFilter={tourIdFilter}
                setTourIdFilter={setTourIdFilter}
                agencyNameFilter={agencyNameFilter}
                setAgencyNameFilter={setAgencyNameFilter}
            />

            {/* --- Edit Tour Modal --- */}
            {isEditTourModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative bg-bg-card border border-gold/20 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 pb-4 border-b border-gold/10">
                            <h3 className="text-xl font-bold text-text">{t('editTourAdmin')}</h3>
                            <button
                                onClick={() => setIsEditTourModalOpen(false)}
                                className="text-text-muted hover:text-gold transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <form
                            onSubmit={handleEditTourSubmit}
                            className="flex flex-col flex-1 overflow-hidden"
                        >
                            <div className="overflow-y-auto px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyName')}</label>
                                    <input
                                        type="text"
                                        className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 opacity-60 cursor-not-allowed"
                                        value={editingTour.title || ''}
                                        readOnly
                                        disabled
                                        title="Cannot be modified by Admin"
                                    />
                                </div>

                                {/* Tour Source Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">Tour Source</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tourSource"
                                                value="url"
                                                checked={editTourSource === 'url'}
                                                onChange={(e) => setEditTourSource(e.target.value)}
                                                className="mr-2 text-gold focus:ring-gold bg-bg-card border-gold/30"
                                            />
                                            <span className="text-sm text-text">{t('externalUrl')}</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tourSource"
                                                value="zip"
                                                checked={editTourSource === 'zip'}
                                                onChange={(e) => setEditTourSource(e.target.value)}
                                                className="mr-2 text-gold focus:ring-gold bg-bg-card border-gold/30"
                                            />
                                            <span className="text-sm text-text">ZIP File</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Conditional Fields based on Tour Source */}
                                {editTourSource === 'url' ? (
                                    <div>
                                        <label className="block text-sm font-medium text-text-muted mb-1">{t('externalUrl')}</label>
                                        <input
                                            type="url"
                                            className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50"
                                            value={editingTour.tour_url || ''}
                                            onChange={e => setEditingTour({ ...editingTour, tour_url: e.target.value })}
                                            placeholder="https://kuula.co/..."
                                        />
                                        <p className="text-xs text-text-muted mt-1">{t('tourUrlHelp')}</p>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-sm font-medium text-text-muted mb-1">Upload New ZIP File</label>
                                        {editingTour.file_path && !editTourFile && (
                                            <p className="text-sm text-text-muted mb-2">
                                                Current: {editingTour.file_path.split('/').pop()}
                                            </p>
                                        )}
                                        <input
                                            ref={editFileInputRef}
                                            type="file"
                                            accept=".zip"
                                            onChange={(e) => setEditTourFile(e.target.files[0])}
                                            className="bg-bg-soft border border-gold/20 text-text-muted rounded px-3 py-2 w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold-gradient file:text-white hover:file:opacity-90"
                                        />
                                        {editTourFile && (
                                            <p className="text-sm text-gold mt-1">
                                                New file selected: {editTourFile.name}
                                            </p>
                                        )}
                                        <p className="text-xs text-text-muted mt-1">
                                            {editTourFile ? 'This will replace the current tour file' : 'Leave empty to keep current file'}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyId')}</label>
                                    <input
                                        type="text"
                                        className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 opacity-60 cursor-not-allowed"
                                        value={editingTour.property_id || ''}
                                        readOnly
                                        disabled
                                        title="Cannot be modified by Admin"
                                    />
                                </div>

                            </div>
                            <div className="p-6 border-t border-gold/10 bg-bg-soft">
                                {uploadProgress > 0 && (
                                    <div className="mb-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-text-muted">{t('uploading')} {uploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-bg-card rounded-full h-2.5 border border-gold/20">
                                            <div className="bg-gold h-2.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditTourModalOpen(false)}
                                        className="bg-transparent py-2 px-4 border border-gold/20 rounded-md shadow-sm text-sm font-medium text-text-muted hover:bg-gold/10 hover:text-gold transition-colors"
                                        disabled={uploadProgress > 0}
                                    >
                                        {t('cancel')}
                                    </button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={uploadProgress > 0}
                                        className={uploadProgress > 0 ? 'opacity-50' : ''}
                                    >
                                        {uploadProgress > 0 ? `${t('uploading')}...` : t('save')}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- Delete Tour Confirmation Modal --- */}
            <ConfirmationModal
                isOpen={isDeleteTourModalOpen}
                onClose={() => {
                    setIsDeleteTourModalOpen(false);
                    setTourToDelete(null);
                }}
                onConfirm={confirmDeleteTour}
                title={t('deleteTour')}
                message={t('confirmDeleteTour')}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                isDanger={true}
            />

            {/* --- Toast --- */}
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.isVisible}
                onClose={() => setToast({ ...toast, isVisible: false })}
                duration={5000}
            />
        </div>
    );
}
